import type { ReactNode } from "react";


export type ToastType = "success" | "error" | "warning" | "info" | "achievement";

export interface ToastOptions {
    type: ToastType;
    title?: string;
    message: string;
    description?: string;
    durationMs?: number;
    icon?: ReactNode;
    includeBorder?: boolean;
    onVisible?: () => void;
}

function generateToastId(): string {
    return `toast-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
}

function getDefaultDuration(type: ToastType): number {
    switch (type) {
        case "success":
            return 3000;
        case "info":
            return 5000;
        case "warning":
            return 5000;
        case "error":
            return 7000;
        case "achievement":
            return 9000;
        default:
            return 7000;
    }
}

export class Toast {
    readonly id: string;
    readonly options: ToastOptions;
    private timerStartTimeMs?: number;
    private timerPauseTimeMs?: number;
    private onVisibleCalled: boolean = false;

    constructor(options: ToastOptions) {
        this.id = generateToastId();
        this.options = {
            ...options,
            durationMs: options.durationMs ?? getDefaultDuration(options.type),
        };
    }

    hasBeenShown(): boolean {
        return this.timerStartTimeMs !== undefined;
    }

    getVisibleDurationMs(): number {
        if (this.timerStartTimeMs === undefined)
            return 0;

        const anchorTimeMs = (
            this.timerPauseTimeMs !== undefined
                ? this.timerPauseTimeMs
                : Date.now()
        );
        return anchorTimeMs - this.timerStartTimeMs;
    }

    shouldBeDismissed(): boolean {
        const durationMs = this.options.durationMs ?? 500;
        return this.getVisibleDurationMs() > durationMs;
    }

    resumeTimer(): void {
        if (this.timerStartTimeMs === undefined) {
            this.timerStartTimeMs = Date.now();
            return;
        }
        if (this.timerPauseTimeMs === undefined)
            return;

        this.timerStartTimeMs = Date.now() - this.getVisibleDurationMs();
        this.timerPauseTimeMs = undefined;
    }

    pauseTimer(): void {
        this.timerPauseTimeMs = Date.now();
    }

    maybeTriggerOnVisible(): boolean {
        if (this.onVisibleCalled)
            return false;

        this.onVisibleCalled = true;
        const { onVisible } = this.options;
        if (onVisible) {
            onVisible();
        }
        return true;
    }
}
